import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useHeroBanners } from '../hooks/useAirtable'

const OverlayBanner = ({ onOpenChat }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [imageLoadStates, setImageLoadStates] = useState({})
  const { t } = useTranslation()
  const { currentLanguage } = useLanguage()
  // Simple image URL function for direct image loading
  const getImageUrl = (path) => path
  
  // Fetch hero banners from Airtable - only active banners
  const { banners: slides, loading, error } = useHeroBanners(true, currentLanguage)

  const totalSlides = slides?.length || 0

  // Auto-play functionality - moved before conditional returns
  useEffect(() => {
    if (totalSlides === 0) return;
    
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true)
        setTimeout(() => {
          setCurrentSlide(prev => (prev + 1) % totalSlides)
        }, 700)
        setTimeout(() => setIsTransitioning(false), 1600)
      }
    }, 7000)

    return () => clearInterval(interval)
  }, [totalSlides, isTransitioning])

  const goToNext = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    // First phase: fade out current content
    setTimeout(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides)
    }, 700) // Change slide after fade out
    setTimeout(() => setIsTransitioning(false), 1600)
  }

  const goToPrevious = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide(prev => (prev === 0 ? totalSlides - 1 : prev - 1))
    }, 700)
    setTimeout(() => setIsTransitioning(false), 1600)
  }

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide(index)
    }, 700)
    setTimeout(() => setIsTransitioning(false), 1600)
  }

  // Enhanced loading state with shimmer
  if (loading) {
    return (
      <section style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #000000 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Shimmer loading effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s infinite'
        }} />
      </section>
    )
  }

  // Show error state or fallback
  if (error || !slides || slides.length === 0) {
    return (
      <section style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #000000 100%)'
      }}>
        <div style={{ fontSize: '18px', color: '#fff', textAlign: 'center' }}>
          <p>{t('common.error')} - Unable to load banner content</p>
          <p style={{ fontSize: '14px', opacity: 0.7 }}>Please check Airtable connection</p>
        </div>
      </section>
    )
  }

  return (
    <section 
      className="overlay-banner-section"
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#000000',
        backgroundImage: `url("${getImageUrl(slides[currentSlide].backgroundImage) || slides[currentSlide].backgroundImage || '/pic/1.jpg'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
    >
      {/* Dark Overlay for Text Readability */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3))',
        zIndex: 1
      }} />

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        disabled={isTransitioning}
        style={{
          position: 'absolute',
          left: '30px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          background: 'rgba(255, 255, 255, 0.1)',
          color: '#ffffff',
          cursor: isTransitioning ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          opacity: isTransitioning ? 0.5 : 1,
          zIndex: 10,
          backdropFilter: 'blur(10px)'
        }}
        onMouseEnter={(e) => {
          if (!isTransitioning) {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)'
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)'
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15,18 9,12 15,6"></polyline>
        </svg>
      </button>

      <button
        onClick={goToNext}
        disabled={isTransitioning}
        style={{
          position: 'absolute',
          right: '30px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          background: 'rgba(255, 255, 255, 0.1)',
          color: '#ffffff',
          cursor: isTransitioning ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          opacity: isTransitioning ? 0.5 : 1,
          zIndex: 10,
          backdropFilter: 'blur(10px)'
        }}
        onMouseEnter={(e) => {
          if (!isTransitioning) {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)'
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)'
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9,18 15,12 9,6"></polyline>
        </svg>
      </button>

      {/* Content Overlay - Text and Button on Image */}
      <div className="overlay-content" style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        textAlign: 'center',
        color: 'white',
        zIndex: 5,
        maxWidth: '800px',
        width: '90%',
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning ? 'translate(-50%, -50%) translateY(60px) scale(0.9)' : 'translate(-50%, -50%) translateY(0) scale(1)',
        transition: 'opacity 0.7s ease-out, transform 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        filter: isTransitioning ? 'blur(4px)' : 'blur(0px)'
      }}>
        <h1 style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 'clamp(32px, 6vw, 72px)',
          fontWeight: 300,
          lineHeight: 1.1,
          marginBottom: '24px',
          textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
          letterSpacing: '-0.02em'
        }}>
          {slides[currentSlide].title}
        </h1>
        
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 'clamp(16px, 3vw, 22px)',
          fontWeight: 300,
          lineHeight: 1.5,
          marginBottom: '40px',
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)',
          color: 'rgba(255, 255, 255, 0.95)',
          maxWidth: '600px',
          margin: '0 auto 40px auto'
        }}>
          {slides[currentSlide].subtitle}
        </p>
        
        <button 
          className="overlay-banner-cta-button"
          style={{
            cursor: 'pointer',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px 36px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            border: '2px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '50px',
            color: 'white',
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            userSelect: 'none',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? 'translateY(30px) scale(0.8)' : 'translateY(0) scale(1)',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.8)'
            e.target.style.transform = 'translateY(-3px) scale(1.08)'
            e.target.style.boxShadow = '0 16px 56px rgba(0, 0, 0, 0.5)'
            // Trigger shine effect
            const shine = e.target.querySelector('.shine-effect')
            if (shine) {
              shine.style.left = '100%'
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)'
            e.target.style.transform = isTransitioning ? 'translateY(30px) scale(0.8)' : 'translateY(0) scale(1)'
            e.target.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)'
            // Reset shine effect
            const shine = e.target.querySelector('.shine-effect')
            if (shine) {
              shine.style.left = '-100%'
            }
          }}
          onMouseDown={(e) => {
            e.target.style.transform = 'translateY(0) scale(0.95)'
          }}
          onMouseUp={(e) => {
            e.target.style.transform = 'translateY(-3px) scale(1.08)'
          }}
          onClick={() => {
            // Check CustumButtonAction field (note: typo in DB field name)
            // If true or 1 → redirect to ButtonUrl
            // If false/null/undefined → open chat
            const currentBanner = slides[currentSlide];
            const customAction = currentBanner?.custom_button_action;
            const buttonUrl = currentBanner?.button_url;
            
            // Check if custom_action is true or 1 (handle boolean, string, and number)
            // Accept: true, 1, "true", "1", "True", "TRUE"
            const shouldRedirect = 
              customAction === true || 
              customAction === 1 || 
              customAction === '1' ||
              String(customAction).toLowerCase() === 'true';
            
            console.log('🔘 OverlayBanner Button Click:', {
              bannerId: currentBanner?.id,
              custom_button_action: customAction,
              custom_button_action_type: typeof customAction,
              button_url: buttonUrl,
              shouldRedirect: shouldRedirect,
              action: shouldRedirect && buttonUrl ? 'Redirect' : 'Open Chat'
            });
            
            if (shouldRedirect && buttonUrl) {
              // Redirect to URL (open in new tab)
              console.log('🔗 Redirecting to:', buttonUrl);
              window.open(buttonUrl, '_blank');
            } else {
              // Default: open chat
              if (!shouldRedirect) {
                console.log('💬 Opening chat (CustumButtonAction is false/null/undefined)');
              } else if (!buttonUrl) {
                console.warn('⚠️ CustumButtonAction is true but ButtonUrl is missing, opening chat instead');
              }
              
              if (onOpenChat) {
                onOpenChat()
              }
            }
          }}
        >
          {/* Button shine effect */}
          <div 
            className="shine-effect"
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
              transition: 'left 0.8s ease',
              pointerEvents: 'none'
            }} 
          />
          
          <span style={{ position: 'relative', zIndex: 2 }}>
            {slides[currentSlide]?.buttonText || 
             (currentLanguage === 'ar' ? slides[currentSlide]?.buttonText_ar : slides[currentSlide]?.buttonText_en) ||
             (currentLanguage === 'ar' ? 'انقر هنا' : 'Click Here')}
          </span>
        </button>
      </div>

      {/* Slide Indicators */}
      <div className="slide-indicators" style={{
        position: 'absolute',
        bottom: '40px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '16px',
        zIndex: 10
      }}>
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            style={{
              width: currentSlide === index ? '40px' : '16px',
              height: '16px',
              borderRadius: '8px',
              border: 'none',
              background: currentSlide === index 
                ? 'rgba(255, 255, 255, 0.9)' 
                : 'rgba(255, 255, 255, 0.4)',
              cursor: isTransitioning ? 'not-allowed' : 'pointer',
              transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              opacity: isTransitioning ? 0.5 : 1,
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
            onMouseEnter={(e) => {
              if (!isTransitioning && currentSlide !== index) {
                e.target.style.background = 'rgba(255, 255, 255, 0.6)'
              }
            }}
            onMouseLeave={(e) => {
              if (currentSlide !== index) {
                e.target.style.background = 'rgba(255, 255, 255, 0.4)'
              }
            }}
          />
        ))}
      </div>

      <style>{`
        /* Enhanced loading and button animations */
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes buttonFadeIn {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .overlay-banner-cta-button:hover .shine-effect {
          left: 100% !important;
        }
        
        /* Mobile responsiveness for overlay banner */
        @media (max-width: 768px) {
          .overlay-content {
            width: 95% !important;
            max-width: 400px !important;
            padding: 0 20px !important;
          }
          
          .overlay-content h1 {
            font-size: clamp(24px, 8vw, 48px) !important;
            line-height: 1.2 !important;
            margin-bottom: 16px !important;
          }
          
          .overlay-content p {
            font-size: clamp(14px, 4vw, 18px) !important;
            margin-bottom: 32px !important;
          }
          
          .overlay-content button {
            padding: 14px 28px !important;
            font-size: 12px !important;
          }
          
          /* Hide arrows on mobile */
          .overlay-banner-section button[style*="left: 30px"],
          .overlay-banner-section button[style*="right: 30px"] {
            display: none !important;
          }
          
          .slide-indicators {
            bottom: 30px !important;
            gap: 12px !important;
          }
          
          .slide-indicators button {
            width: currentSlide === index ? '32px' : '12px' !important;
            height: 12px !important;
            border-radius: 6px !important;
          }
        }
      `}</style>
    </section>
  )
}

export default OverlayBanner
